import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useState } from "react";

import AutocompleteSelect from "@/common/components/Autocomplete/AutocompleteSelect";
import { ConfirmationDialog } from "@/common/components/ConfirmationDialog";
import { EnvironmentPicker } from "@/common/components/EnvironmentPicker";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useDebounce } from "@/common/hooks/useDebounce";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import { useFeatureTargetingContextAttributeKeys } from "@/feature/data/useFeatureTargetingContextAttributesData";

import {
  backgroundOperationsQueryKey,
  useBackgroundOperations,
} from "./BackgroundOperations";

type EntityType = "company" | "user";
const entityLabels: Record<EntityType, string> = {
  company: "Company",
  user: "User",
};
type DeletionTarget = {
  appId: string;
  envId: string;
  envName: string;
  entityType: EntityType;
  key: string;
};

export function AttributeDeletionDialog({
  entityType,
  onClose,
}: {
  entityType: EntityType;
  onClose: () => void;
}) {
  const { appId, app, envId } = useEnvironment();
  const [targetEnvId, setTargetEnvId] = useState(envId);
  const [key, setKey] = useState("");
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<DeletionTarget>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const errorToast = useErrorToast();
  const operations = useBackgroundOperations(appId, targetEnvId);
  const hasActiveDeletion =
    operations.data?.some(
      (operation) =>
        operation.type === "entity-attribute-deletion" &&
        (operation.status === "queued" || operation.status === "running"),
    ) ?? false;
  const filter = useDebounce(search, 250);
  const {
    data = [],
    isFetching,
    isPlaceholderData,
    isError,
  } = useFeatureTargetingContextAttributeKeys(entityType, filter, targetEnvId);
  const attributes = data.filter((attribute) => !attribute.system);
  const selected = attributes.find((attribute) => attribute.key === key);
  const canDelete =
    !!appId &&
    !!targetEnvId &&
    !!selected &&
    !isFetching &&
    !isPlaceholderData &&
    !isError &&
    !operations.isPending &&
    !operations.isError &&
    !hasActiveDeletion;

  const { mutate: deleteAttribute, isPending: isDeleting } = useMutation({
    networkMode: "always", // Fail rather than queue offline.
    mutationFn: async ({ appId, envId, entityType, key }: DeletionTarget) => {
      await api.delete<"/apps/:appId/envs/:envId/attributes">(
        `/apps/${appId}/envs/${envId}/attributes`,
        { data: { entityType, key } },
      );
    },
    onSuccess: async (_, target) => {
      await queryClient.invalidateQueries({
        queryKey: backgroundOperationsQueryKey(target.appId, target.envId),
      });
      toast({
        status: "success",
        title: "Attribute deletion started",
        description:
          "Deletion runs in the background. Check its status below; you can leave this page and return later.",
      });
      setKey("");
      setPending(undefined);
      onClose();
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        errorToast({
          title:
            "An attribute deletion is already queued or running in this environment",
        });
        setPending(undefined);
      } else {
        errorToast({ title: "Failed to start attribute deletion" });
      }
      void queryClient.invalidateQueries({
        queryKey: backgroundOperationsQueryKey(appId, targetEnvId),
      });
    },
  });
  const close = () => {
    if (!isDeleting) setPending(undefined);
  };

  return (
    <>
      <Modal isOpen={!pending} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete {entityType} attribute</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text color="dimmed" fontSize="sm">
                Permanently remove an attribute from every stored {entityType}{" "}
                in an environment. Processing large environments can take a
                while.
              </Text>
              <FormControl>
                <FormLabel>Environment</FormLabel>
                <EnvironmentPicker
                  value={targetEnvId}
                  size="sm"
                  onChange={(id) => {
                    if (!id) return;
                    setTargetEnvId(id);
                    setKey("");
                    setSearch("");
                    setPending(undefined);
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="attribute-deletion-key">
                  Attribute
                </FormLabel>
                <AutocompleteSelect
                  key={targetEnvId}
                  id="attribute-deletion-key"
                  value={selected ?? null}
                  suggestions={isPlaceholderData ? [] : attributes}
                  itemKeyProperty="key"
                  itemRenderFn={(attribute) => attribute.label || attribute.key}
                  placeholder="Select attribute"
                  isLoadingSuggestions={isFetching}
                  onSearchInput={setSearch}
                  onChange={(attribute) => setKey(attribute?.key ?? "")}
                  w="full"
                />
              </FormControl>
              {isError && (
                <Text color="red.500" fontSize="sm">
                  Could not load attributes. Try again.
                </Text>
              )}
              {operations.isError && (
                <Text color="red.500" fontSize="sm">
                  Could not refresh background operation status. Try again.
                </Text>
              )}
              {hasActiveDeletion && (
                <Text fontSize="sm">
                  An attribute deletion is already queued or running in this
                  environment. Wait for it to complete or fail before starting
                  another.
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              isDisabled={!canDelete || isDeleting}
              onClick={() => {
                if (appId && targetEnvId)
                  setPending({
                    appId,
                    envId: targetEnvId,
                    envName:
                      app?.environments.find((env) => env.id === targetEnvId)
                        ?.name ?? targetEnvId,
                    entityType,
                    key,
                  });
              }}
            >
              Delete attribute
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ConfirmationDialog
        isOpen={!!pending}
        title="Delete attribute?"
        confirmLabel="Delete attribute"
        confirmColor="red"
        isLoading={isDeleting}
        loadingText="Starting deletion"
        onConfirm={() => {
          if (pending && !isDeleting) deleteAttribute(pending);
        }}
        onCancel={close}
        onClose={close}
        closeOnEsc={!isDeleting}
        closeOnOverlayClick={!isDeleting}
        description={
          <Text>
            Permanently delete <strong>{pending?.key}</strong> from all stored{" "}
            {pending && entityLabels[pending.entityType].toLowerCase()} data in{" "}
            <strong>{pending?.envName}</strong>?
          </Text>
        }
        warning={
          <Alert alignItems="flex-start" status="warning">
            <AlertIcon />
            <Text>
              This can affect targeting if relying on stored attributes. New
              incoming data may add the attribute back again.
            </Text>
          </Alert>
        }
      />
    </>
  );
}
